// src/pages/PrivacyPage.jsx
import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

export default function PrivacyPage({ language = "en" }) {
  const navigate = useNavigate();

  const t = {
    en: {
      title: "Privacy Policy — YourLostAndFound",
      desc:
        "Pre-launch Privacy Policy for YourLostAndFound: minimal data collection (name, email) for updates and inquiries, no third-party selling, deletion on request.",
      h1: "Privacy Policy — Pre-Launch Phase",
      updated: "Last updated: November 2025",
      body: [
        "YourLostAndFound is currently in a pre-launch phase. During this period, we collect only minimal information from visitors who voluntarily join our waitlist or contact us through our forms.",
        "What we collect: full name and email address (for communication purposes).",
        "How we use this information: to provide updates about our upcoming launch and related news, and to respond to direct inquiries submitted via the contact form.",
        "We do not share, sell, or transfer your data to third parties. We may use trusted service providers (e.g., hosting or analytics) acting on our behalf under confidentiality and solely to operate the Site.",
        "Security: we use reasonable safeguards to protect information, but no transmission or storage can be guaranteed to be 100% secure.",
        "Once the platform becomes operational in 2026, this notice will be replaced by our full Privacy Policy, covering user accounts, cookies, retention, third parties, and user rights in detail.",
        "If you wish to have your information deleted before our launch, please contact: privacy@yourlostandfound.com"
      ]
    },
    es: {
      title: "Política de Privacidad — YourLostAndFound",
      desc:
        "Política de Privacidad en pre-lanzamiento: recopilación mínima (nombre y email) para actualizaciones y consultas; sin venta a terceros; eliminación a solicitud.",
      h1: "Política de Privacidad — Fase Pre-Lanzamiento",
      updated: "Última actualización: Noviembre de 2025",
      body: [
        "YourLostAndFound se encuentra en fase previa al lanzamiento. Durante este periodo, recopilamos únicamente la información mínima de los visitantes que se registran voluntariamente en la lista de espera o nos contactan mediante formularios.",
        "Información que recopilamos: nombre completo y correo electrónico (con fines de comunicación).",
        "Uso de la información: enviar actualizaciones sobre el lanzamiento y noticias relacionadas, y responder a consultas enviadas a través del formulario de contacto.",
        "No compartimos, vendemos ni transferimos tu información a terceros. Podemos utilizar proveedores de confianza (p. ej., hosting o analítica) que actúan en nuestro nombre bajo confidencialidad y únicamente para operar el Sitio.",
        "Seguridad: aplicamos medidas razonables para proteger la información, aunque ninguna transmisión o almacenamiento es 100% seguro.",
        "Cuando la plataforma esté operativa en 2026, este aviso será reemplazado por nuestra Política de Privacidad completa, que cubrirá cuentas de usuario, cookies, retención de datos, terceros y derechos del usuario.",
        "Si deseas eliminar tu información antes del lanzamiento, contáctanos en: privacy@yourlostandfound.com"
      ]
    },
    pt: {
      title: "Política de Privacidade — YourLostAndFound",
      desc:
        "Política de Privacidade em pré-lançamento: coleta mínima (nome e email) para atualizações e contato; sem venda a terceiros; exclusão mediante solicitação.",
      h1: "Política de Privacidade — Fase Pré-Lançamento",
      updated: "Última atualização: Novembro de 2025",
      body: [
        "O YourLostAndFound está em fase pré-lançamento. Durante este período, coletamos apenas informações mínimas de visitantes que se cadastram voluntariamente na lista de espera ou entram em contato por meio de formulários.",
        "Informações coletadas: nome completo e endereço de e-mail (para fins de comunicação).",
        "Como usamos as informações: enviar atualizações sobre o lançamento e notícias relacionadas, e responder às mensagens enviadas pelo formulário de contato.",
        "Não compartilhamos, vendemos nem transferimos seus dados a terceiros. Podemos usar provedores confiáveis (ex.: hosting ou analytics) que atuam em nosso nome sob confidencialidade e exclusivamente para operar o Site.",
        "Segurança: adotamos medidas razoáveis de proteção, embora nenhuma transmissão ou armazenamento seja 100% seguro.",
        "Quando a plataforma estiver ativa em 2026, este aviso será substituído por nossa Política de Privacidade completa, abrangendo contas de usuário, cookies, retenção, terceiros e direitos do usuário.",
        "Se desejar excluir seus dados antes do lançamento, entre em contato: privacy@yourlostandfound.com"
      ]
    }
  }[language];

  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://yourlostandfound.com";
  const canonical = `${origin}/privacy`;

  return (
    <>
      {/* Cabecera visual igual a Contact/Terms */}
      <section
        id="privacy"
        className="bg-[#48887B] py-12 text-center scroll-mt-[120px] pt-[120px]"
      >
        <h2 className="text-white text-3xl md:text-5xl font-bold">
          {t.h1}
        </h2>
      </section>

      <main className="max-w-3xl mx-auto py-12 px-6">
        <Helmet>
          <title>{t.title}</title>
          <meta name="description" content={t.desc} />
          <link rel="canonical" href={canonical} />
          {/* hreflang alternates (ajústalos si usas dominios diferentes por idioma) */}
          <link rel="alternate" hrefLang="en" href={`${origin}/privacy`} />
          <link rel="alternate" hrefLang="es" href={`${origin}/privacy`} />
          <link rel="alternate" hrefLang="pt" href={`${origin}/privacy`} />
          <meta property="og:title" content={t.title} />
          <meta property="og:description" content={t.desc} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={canonical} />
          <meta name="twitter:card" content="summary" />
        </Helmet>

        <p className="text-gray-500 mb-6">{t.updated}</p>

        {t.body.map((p, i) => (
          <p key={i} className="mb-4 text-gray-800 leading-relaxed">
            {p}
          </p>
        ))}

        <button
          onClick={() => navigate('/')}
          className="mt-8 px-6 py-2 bg-[#48887B] text-white rounded-lg hover:bg-[#3b726a] transition"
        >
          {language === 'es' ? 'Volver' : language === 'pt' ? 'Voltar' : 'Back'}
        </button>
      </main>
    </>
  );
}
